import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatChipsModule } from '@angular/material/chips';
import { NotificationService } from '../../../../../services/notification.service';
import {
  ConfirmationModalComponent,
  ConfirmationModalData,
} from '../../../../../shared/components/confirmation-modal/confirmation-modal.component';
import {
  AdminLocationRow,
  LocationSettingsService,
} from '../../../../services/location-settings.service';
import { PackageMappingsComponent } from './package-mappings/package-mappings.component';

interface WaySaveContext {
  companyId: string;
  location: AdminLocationRow;
}

const CLEAR_WAY_CONFIRMATION: ConfirmationModalData = {
  title: 'Clear Way vendor location ID?',
  description: 'Way purchase flows will be disabled for this location until a new ID is saved.',
  cancelText: 'Cancel',
  confirmText: 'Clear',
  showTimeInfo: false,
};

/**
 * Way marketplace settings of one location (company detail → Location → Way):
 * the Way vendor location id and the Way package mappings. Moved from the former
 * Location Settings page.
 */
@Component({
  selector: 'app-location-way',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatChipsModule,
    PackageMappingsComponent,
    ConfirmationModalComponent,
  ],
  templateUrl: './location-way.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./location-way.component.scss'],
})
export class LocationWayComponent implements OnChanges {
  @Input() companyId = '';
  @Input() location: AdminLocationRow | null = null;
  /** The saved row, so the parent's location cache stays in step. */
  @Output() locationUpdated = new EventEmitter<AdminLocationRow>();

  wayVendorLocationId = '';
  savedWayVendorLocationId: string | null = null;
  isSavingWay = false;
  wayFieldError = '';
  showClearWayConfirmation = false;
  clearWayConfirmationData: ConfirmationModalData = { ...CLEAR_WAY_CONFIRMATION };
  /** The location the open Clear dialog was opened for; the confirm acts on it only. */
  private clearWayContext: WaySaveContext | null = null;

  private readonly locationSettingsService = inject(LocationSettingsService);
  private readonly messageService = inject(NotificationService);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['location'] || changes['companyId']) {
      const previous = changes['location']?.previousValue as AdminLocationRow | null | undefined;
      // A refreshed row for the same location after our own save must not wipe a draft.
      if (!changes['location'] || previous?.id !== this.location?.id || changes['companyId']) {
        this.applyWayFormFromLocation();
        this.closeClearDialogForOtherLocation();
      }
    }
  }

  get locationId(): string {
    return this.location?.id || '';
  }

  get wayConfigured(): boolean {
    return Boolean(this.savedWayVendorLocationId?.trim());
  }

  get wayDirty(): boolean {
    const current = this.wayVendorLocationId.trim();
    const saved = (this.savedWayVendorLocationId || '').trim();
    return current !== saved;
  }

  private applyWayFormFromLocation(): void {
    const value = this.location?.way_vendor_location_id?.trim() || '';
    this.wayVendorLocationId = value;
    this.savedWayVendorLocationId = value || null;
    this.wayFieldError = '';
  }

  /** A location switch closes a Clear dialog opened for the previous location. */
  private closeClearDialogForOtherLocation(): void {
    const context = this.clearWayContext;
    if (!context || this.clearWayConfirmationData.isLoading) {
      return;
    }
    if (context.location.id !== this.locationId || context.companyId !== this.companyId) {
      this.showClearWayConfirmation = false;
      this.clearWayContext = null;
    }
  }

  onClearWaySettings(): void {
    if (!this.wayConfigured || this.isSavingWay || !this.companyId || !this.location) {
      return;
    }

    this.clearWayContext = { companyId: this.companyId, location: this.location };
    this.clearWayConfirmationData = { ...CLEAR_WAY_CONFIRMATION, isLoading: false };
    this.showClearWayConfirmation = true;
  }

  onClearWayCancel(): void {
    if (this.clearWayConfirmationData.isLoading) {
      return;
    }
    this.wayVendorLocationId = this.savedWayVendorLocationId?.trim() || '';
    this.wayFieldError = '';
    this.showClearWayConfirmation = false;
    this.clearWayContext = null;
  }

  onClearWayConfirm(): void {
    const context = this.clearWayContext;
    if (!context || this.clearWayConfirmationData.isLoading || this.isSavingWay) {
      return;
    }

    this.wayVendorLocationId = '';
    this.wayFieldError = '';
    this.clearWayConfirmationData = {
      ...this.clearWayConfirmationData,
      isLoading: true,
    };
    this.onSaveWaySettings({ fromClearConfirmation: true, context });
  }

  onSaveWaySettings(options?: { fromClearConfirmation?: boolean; context?: WaySaveContext }): void {
    const context =
      options?.context ??
      (this.companyId && this.location
        ? { companyId: this.companyId, location: this.location }
        : null);
    if (!context || this.isSavingWay) {
      return;
    }

    const trimmed = options?.fromClearConfirmation ? '' : this.wayVendorLocationId.trim();
    // Clearing an existing Way ID must go through the same confirmation as Clear.
    if (!trimmed && this.wayConfigured && !options?.fromClearConfirmation) {
      this.onClearWaySettings();
      return;
    }

    const companyId = context.companyId;
    const location = context.location;
    const locationId = location.id;
    this.wayFieldError = '';
    this.isSavingWay = true;

    const payload = { way_vendor_location_id: trimmed || null };
    this.locationSettingsService.updateWaySettings(companyId, locationId, payload).subscribe({
      next: response => {
        this.isSavingWay = false;
        if (response.status) {
          const saved = response.data?.way_vendor_location_id?.trim() || null;
          this.locationUpdated.emit({ ...location, way_vendor_location_id: saved });

          // Only sync the form if the admin is still on the location that was saved.
          if (this.companyId === companyId && this.locationId === locationId) {
            this.savedWayVendorLocationId = saved;
            this.wayVendorLocationId = saved || '';
          }

          if (options?.fromClearConfirmation) {
            this.showClearWayConfirmation = false;
            this.clearWayContext = null;
            this.clearWayConfirmationData = {
              ...CLEAR_WAY_CONFIRMATION,
              isLoading: false,
            };
          }

          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: saved ? 'Way vendor location ID saved' : 'Way vendor location ID cleared',
          });
        } else {
          if (options?.fromClearConfirmation) {
            this.clearWayConfirmationData = {
              ...this.clearWayConfirmationData,
              isLoading: false,
            };
          }
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.message || 'Failed to save Way settings',
          });
        }
      },
      error: err => {
        this.isSavingWay = false;
        if (options?.fromClearConfirmation) {
          this.clearWayConfirmationData = {
            ...this.clearWayConfirmationData,
            isLoading: false,
          };
        }
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.message || 'Failed to save Way settings',
        });
      },
    });
  }
}
